import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/api/client';

type IntervalId = ReturnType<typeof setInterval>;

import type { SubscriptionTier } from '@/types';

// User type from Keycloak token + backend sync
export interface User {
  sub: string;
  email: string;
  displayName: string;
  given_name?: string;
  family_name?: string;
  roles: string[];
  // From backend after JIT provisioning
  id?: string;
  avatarUrl?: string;
  creditsBalance?: number;
  tier?: SubscriptionTier;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  register: () => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  syncUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Keycloak configuration
const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_AUTHORITY || 'https://auth.applantus.com/realms/applantus';
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'spark-app';

const TOKEN_REFRESH_INTERVAL = 4 * 60 * 1000; // 4 minutes (tokens expire in 5 mins)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const refreshTimerRef = useRef<IntervalId | null>(null);
  const isProcessingCallback = useRef(false);

  // Start token refresh timer
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    refreshTimerRef.current = setInterval(async () => {
      const refreshToken = localStorage.getItem('spark_refresh_token');
      if (refreshToken) {
        try {
          await refreshAccessToken(refreshToken);
        } catch (err) {
          console.error('Token refresh failed:', err);
        }
      }
    }, TOKEN_REFRESH_INTERVAL);
  }, []);

  // Stop token refresh timer
  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Sync user with backend (JIT provisioning)
  const syncUser = useCallback(async () => {
    try {
      const backendUser = await api.get<{
        id: string;
        email: string;
        displayName: string;
        avatarUrl?: string;
        role: string;
        subscriptionTier: string;
        creditsBalance: number;
      }>('/api/auth/me');

      setUser(prev => prev ? {
        ...prev,
        id: backendUser.id,
        avatarUrl: backendUser.avatarUrl,
        creditsBalance: backendUser.creditsBalance,
        tier: backendUser.subscriptionTier as SubscriptionTier,
      } : null);
    } catch (err) {
      console.error('Failed to sync user with backend:', err);
    }
  }, []);

  const exchangeCodeForToken = async (code: string): Promise<boolean> => {
    const codeVerifier = sessionStorage.getItem('spark_pkce_code_verifier');
    if (!codeVerifier) {
      console.error('Code verifier not found');
      return false;
    }

    const tokenEndpoint = `${KEYCLOAK_URL}/protocol/openid-connect/token`;
    const redirectUri = `${window.location.origin}/callback`;

    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', KEYCLOAK_CLIENT_ID);
    params.append('code', code);
    params.append('redirect_uri', redirectUri);
    params.append('code_verifier', codeVerifier);

    try {
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Token exchange failed:', errorData);
        return false;
      }

      const data = await response.json();
      const token = data.access_token;

      localStorage.setItem('spark_access_token', token);
      if (data.refresh_token) {
        localStorage.setItem('spark_refresh_token', data.refresh_token);
      }
      if (data.id_token) {
        localStorage.setItem('spark_id_token', data.id_token);
      }

      api.setAccessToken(token);
      setAccessToken(token);
      sessionStorage.removeItem('spark_pkce_code_verifier');

      const decoded = decodeToken(token);
      const userFromToken: User = {
        sub: decoded.sub,
        email: decoded.email || '',
        displayName: decoded.name || decoded.preferred_username || decoded.email || 'User',
        given_name: decoded.given_name,
        family_name: decoded.family_name,
        roles: decoded.spark_roles || [],
      };
      setUser(userFromToken);
      startRefreshTimer();

      return true;
    } catch (err) {
      console.error('Token exchange error:', err);
      return false;
    }
  };

  const refreshAccessToken = async (refreshToken: string) => {
    const tokenEndpoint = `${KEYCLOAK_URL}/protocol/openid-connect/token`;

    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('client_id', KEYCLOAK_CLIENT_ID);
    params.append('refresh_token', refreshToken);

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    const token = data.access_token;

    localStorage.setItem('spark_access_token', token);
    if (data.refresh_token) {
      localStorage.setItem('spark_refresh_token', data.refresh_token);
    }
    if (data.id_token) {
      localStorage.setItem('spark_id_token', data.id_token);
    }

    api.setAccessToken(token);
    setAccessToken(token);

    const decoded = decodeToken(token);
    setUser(prev => ({
      ...prev!,
      sub: decoded.sub,
      email: decoded.email || '',
      displayName: decoded.name || decoded.preferred_username || decoded.email || 'User',
      given_name: decoded.given_name,
      family_name: decoded.family_name,
      roles: decoded.spark_roles || [],
    }));
  };

  useEffect(() => {
    const init = async () => {
      // Handle OAuth callback
      if (window.location.pathname === '/callback') {
        // Prevent double processing
        if (isProcessingCallback.current) {
          return;
        }
        isProcessingCallback.current = true;

        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');

        if (code) {
          const success = await exchangeCodeForToken(code);
          if (success) {
            // Sync with backend
            await syncUser();
            // Get return URL and redirect
            const returnUrl = sessionStorage.getItem('spark_return_url') || '/dashboard';
            sessionStorage.removeItem('spark_return_url');
            // Use window.location for reliable redirect
            window.location.href = returnUrl;
            return;
          }
        }

        if (error) {
          console.log('Auth error:', error);
        }

        // Failed or error - redirect to landing
        window.location.href = '/';
        return;
      }

      // Check for stored token
      const storedToken = localStorage.getItem('spark_access_token');

      if (storedToken && !isTokenExpired(storedToken)) {
        api.setAccessToken(storedToken);
        setAccessToken(storedToken);
        try {
          const decoded = decodeToken(storedToken);
          setUser({
            sub: decoded.sub,
            email: decoded.email || '',
            displayName: decoded.name || decoded.preferred_username || decoded.email || 'User',
            given_name: decoded.given_name,
            family_name: decoded.family_name,
            roles: decoded.spark_roles || [],
          });
          startRefreshTimer();
          syncUser();
        } catch (err) {
          console.error('Error decoding stored token:', err);
          localStorage.removeItem('spark_access_token');
          localStorage.removeItem('spark_refresh_token');
        }
      } else if (storedToken) {
        // Token expired, try refresh
        const refreshToken = localStorage.getItem('spark_refresh_token');
        if (refreshToken) {
          try {
            await refreshAccessToken(refreshToken);
            startRefreshTimer();
            syncUser();
          } catch {
            localStorage.removeItem('spark_access_token');
            localStorage.removeItem('spark_refresh_token');
          }
        } else {
          localStorage.removeItem('spark_access_token');
        }
      }

      setIsLoading(false);
      setIsInitialized(true);
    };

    init();

    return () => {
      stopRefreshTimer();
    };
  }, [startRefreshTimer, stopRefreshTimer, syncUser]);

  const login = useCallback(async () => {
    // Store current path for return after login
    const currentPath = window.location.pathname;
    if (currentPath !== '/' && currentPath !== '/callback') {
      sessionStorage.setItem('spark_return_url', currentPath);
    } else {
      sessionStorage.setItem('spark_return_url', '/dashboard');
    }

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    sessionStorage.setItem('spark_pkce_code_verifier', codeVerifier);

    const authEndpoint = `${KEYCLOAK_URL}/protocol/openid-connect/auth`;
    const redirectUri = `${window.location.origin}/callback`;

    const params = new URLSearchParams({
      client_id: KEYCLOAK_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid profile email',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${authEndpoint}?${params.toString()}`;
  }, []);

  const register = useCallback(async () => {
    sessionStorage.setItem('spark_return_url', '/dashboard');

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    sessionStorage.setItem('spark_pkce_code_verifier', codeVerifier);

    const authEndpoint = `${KEYCLOAK_URL}/protocol/openid-connect/registrations`;
    const redirectUri = `${window.location.origin}/callback`;

    const params = new URLSearchParams({
      client_id: KEYCLOAK_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid profile email',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${authEndpoint}?${params.toString()}`;
  }, []);

  const logout = useCallback(() => {
    stopRefreshTimer();

    // Get id_token before clearing storage (needed for Keycloak logout)
    const idToken = localStorage.getItem('spark_id_token');

    localStorage.removeItem('spark_access_token');
    localStorage.removeItem('spark_refresh_token');
    localStorage.removeItem('spark_id_token');
    sessionStorage.removeItem('spark_pkce_code_verifier');
    sessionStorage.removeItem('spark_return_url');
    api.setAccessToken(null);
    setAccessToken(null);
    setUser(null);

    const logoutEndpoint = `${KEYCLOAK_URL}/protocol/openid-connect/logout`;
    const redirectUri = window.location.origin;

    const params = new URLSearchParams({
      post_logout_redirect_uri: redirectUri,
      client_id: KEYCLOAK_CLIENT_ID,
    });

    // Add id_token_hint if available (required for proper Keycloak logout)
    if (idToken) {
      params.append('id_token_hint', idToken);
    }

    window.location.href = `${logoutEndpoint}?${params.toString()}`;
  }, [stopRefreshTimer]);

  const hasRole = useCallback((role: string) => {
    return user?.roles?.includes(role.toLowerCase()) ?? false;
  }, [user]);

  const hasAnyRole = useCallback((roles: string[]) => {
    return roles.some(role => user?.roles?.includes(role.toLowerCase()));
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      accessToken,
      isLoading,
      isInitialized,
      isAuthenticated: !!accessToken && !!user,
      login,
      register,
      logout,
      hasRole,
      hasAnyRole,
      syncUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

// PKCE helpers
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

function base64UrlEncode(array: Uint8Array): string {
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(array)));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

interface DecodedToken {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  spark_roles?: string[];
  exp: number;
}

function decodeToken(token: string): DecodedToken {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload) as DecodedToken;
}

function isTokenExpired(token: string): boolean {
  try {
    const decoded = decodeToken(token);
    const exp = decoded.exp;
    const currentTime = Math.floor(Date.now() / 1000);
    return exp < currentTime + 60;
  } catch {
    return true;
  }
}
